import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { APIRequestState } from 'constants/API'
import { ActionPage } from 'components/common/ActionPage/ActionPage.component'
import { EditProcessingPopup } from './EditProcessingPopup'
import { EditorOptionExporting } from './EditorOptions/EditorOptionExporting'
import { Path } from 'constants/router'
import { PropertyTab } from '../Property/ClientProperty/_main/contexts'
import { VisualEditingSettings } from './VisualEditingSettings'
import { VisualEditingSteps } from 'constants/misc'
import { VisualsEditingDownloadPopup } from './VisualsEditingDownloadPopup'
import { VisualsEditingFooter } from './VisualEditingFooter'
import { VisualsEditingPreviewGallery } from 'components/pages/VisualsEditing/VisualsEditingPreviewGallery.module'
import { VisualsEditingSaveTemplatePopup } from './VisualsEditingSaveTemplatePopup'
import { VisualsEditingSubscribePreview } from './VisualsEditingSubscribePreview'
import { exportTypeToEditedVisualsType } from 'constants/visualEditing'
import { getPropertyPathname } from 'utils/helpers/getPropertyPathname'
import styles from 'components/pages/VisualsEditing/VisualsEditing.module.sass'
import { useActionPopup, useWorkspacePlan } from 'utils/hooks'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'
import { useVisualsEditing } from 'components/pages/VisualsEditing/VisualsEditing.context'
import { useVisualsEditingDownload } from './VisualsEditingDownload.context'

/**
 * Visual editing controller for selectedVisualsIds from Gallery
 * @example <VisualsEditingController />
 */
export const VisualsEditingController: FC = () => {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const isPropertyPath = getPropertyPathname(location.pathname)

  const { t } = useTranslation(['visuals_editing'])
  const { showConfirm } = useActionPopup()
  const { currentUserWorkspace } = useUserData()
  const { archiveEditingVisualsMutation } = useVisualsEditingDownload()
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()

  const {
    assignmentId,
    showExitPopup,
    logEditEvent,
    activeFlowStep,
    setActiveFlowStep,
    visualEditingFilenames,
    exportOption,
    retryDownload,
    editingJobId,
    setSaveTemplateStatusSuccess,
    triggerVisualEditing,
    jobPoll,
    loadedEditedVisuals,
    setLoadedEditedVisuals,
  } = useVisualsEditing()

  const flowStepperSteps = Object.values(VisualEditingSteps).filter(value => typeof value === 'string') as Array<string>
  const loadedEditedVisualsResponse = loadedEditedVisuals[editingJobId]
  const isLoadVisualFailed = jobPoll.isError
  const isSubmitFailed = triggerVisualEditing.isError
  const showRequestError = isSubmitFailed || isLoadVisualFailed
  const errorStatusResponse = isSubmitFailed ? triggerVisualEditing : jobPoll

  const [editProcessingPopup, setEditProcessingPopup] = useState(false)
  const [downloadPopup, setDownloadPopup] = useState(false)
  const [saveTemplatePopup, setSaveTemplatePopup] = useState(false)

  const onActionPageClose = useCallback(async () => {
    switch (activeFlowStep) {
      case VisualEditingSteps.SETTINGS:
        logEditEvent('Exit Bulk Editing')
        if (
          showExitPopup &&
          !(await showConfirm(t('exit_edit_popup.content'), {
            title: t('exit_edit_popup.title'),
            confirmAcceptText: t('exit_edit_popup.confirm_accept'),
            confirmDenyText: t('exit_edit_popup.confirm_deny')
          }))
        ) return
        break
      case VisualEditingSteps.PREVIEW:
        logEditEvent('Cancel Download Bulk Editing')
        if (
          !(await showConfirm(t('exit_preview_popup.content'), {
            title: t('exit_preview_popup.title'),
            confirmAcceptText: t('exit_preview_popup.confirm_accept'),
            confirmDenyText: t('exit_preview_popup.confirm_deny')
          }))
        ) return
        break
    }

    if (isPropertyPath) {
      navigate(Path.PROPERTY_TAB_ASSIGNMENT.replace(':id', id).replace(':tab', PropertyTab.GALLERY).replace(':assignmentId', assignmentId.toString()))
    } else {
      navigate(Path.GALLERY.replace(':id', assignmentId.toString()))
    }
  }, [activeFlowStep, isPropertyPath, logEditEvent, showExitPopup, showConfirm, t, navigate, id, assignmentId])

  // we want to check if any of the files in response exist with OK request state before we move user to Preview
  const isPreviewReady = useMemo(() => Object.values(loadedEditedVisualsResponse || {}).some(
    file => file?.[exportTypeToEditedVisualsType[exportOption]]?.request?.state === APIRequestState.OK
  ), [loadedEditedVisualsResponse, exportOption])

  const closeProcessingPopup = () => {
    if (!currentUserWorkspace) return
    setEditProcessingPopup(false)
    setLoadedEditedVisuals({})
  }

  const onEditingExit = useCallback(() => {
    logEditEvent('Exit Visuals Editing Download')
    setLoadedEditedVisuals({})
    archiveEditingVisualsMutation.reset()

    if (isPropertyPath) {
      navigate(Path.PROPERTY_TAB_ASSIGNMENT.replace(':id', id).replace(':tab', PropertyTab.GALLERY).replace(':assignmentId', assignmentId.toString()))
    } else {
      navigate(Path.GALLERY.replace(':id', assignmentId.toString()))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logEditEvent, setLoadedEditedVisuals, isPropertyPath, navigate, id, assignmentId])

  useEffect(() => {
    if (isPreviewReady && triggerVisualEditing.isSuccess) {
      setActiveFlowStep(VisualEditingSteps.PREVIEW)
      setEditProcessingPopup(false)
    }
  }, [isPreviewReady, setActiveFlowStep, triggerVisualEditing.isSuccess])

  // Reset VE result when exit the VE page
  useEffect(() => {
    return () => {
      setActiveFlowStep(VisualEditingSteps.SETTINGS)
      archiveEditingVisualsMutation.reset()
      setLoadedEditedVisuals({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ActionPage
      id={`visual-editing-assignment-${assignmentId}`}
      title={t('title')}
      subtitle={t('n_visuals_selected', { count: visualEditingFilenames.length })}
      onClose={onActionPageClose}
      flowStepperSteps={flowStepperSteps}
      flowStepperActiveStep={activeFlowStep}
      footerChildren={
        <VisualsEditingFooter
          setEditProcessingPopup={setEditProcessingPopup}
          setSaveTemplatePopup={setSaveTemplatePopup}
          setDownloadPopup={setDownloadPopup}
        />
      }
    >
      <div className={styles.children}>

        {/* VISUALS EDITING SETTINGS STEP */}
        {activeFlowStep === VisualEditingSteps.SETTINGS &&
          <Fragment>

            <VisualsEditingPreviewGallery className={styles.leftContainer} justifyContent='left' alignItems='flex-start' />

            <div className={styles.rightContainer}>
              <div className={styles.header}>
                <div className={styles.title}>{t('editor_options.header')}</div>
              </div>
              <div className={styles.body}>

                {hasWorkspaceActiveSubscription
                  ? <VisualEditingSettings />
                  : <VisualsEditingSubscribePreview />
                }

                {/** Exporting options, visible for all users */}
                <EditorOptionExporting />

              </div>
            </div>

            <EditProcessingPopup
              isOpen={editProcessingPopup}
              isError={showRequestError}
              query={errorStatusResponse}
              onClose={closeProcessingPopup}
            />

          </Fragment>}

        {/* VISUALS EDITING PREVIEW STEP */}
        {activeFlowStep === VisualEditingSteps.PREVIEW && <VisualsEditingPreviewGallery className={styles.editedPreview} justifyContent='flex-start' />}

        {/* Visuals Editing Download Popup */}
        <VisualsEditingDownloadPopup
          isOpen={downloadPopup}
          retryDownload={retryDownload}
          onEditingExit={onEditingExit}
          onClickClose={() => setDownloadPopup(false)}
        />

        {/* Visuals Editing Save Template Popup */}
        <VisualsEditingSaveTemplatePopup
          isOpen={saveTemplatePopup}
          onClose={() => setSaveTemplatePopup(false)}
          currentUserWorkspace={currentUserWorkspace}
          onSaved={() => { setSaveTemplatePopup(false); setSaveTemplateStatusSuccess(true) }}
        />
      </div>
    </ActionPage>
  )
}
